import { useEffect, useRef } from "react";
import type { VideoCanvasObject } from "@refinement-canvas/domain";
import { useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";

interface Props {
  readonly object: VideoCanvasObject;
}

// 動画オブジェクト（MP4 / WebM 等）。
// ビューポート外に出た要素は IntersectionObserver で pause() し、CPU を節約する。
// クリックで選択、ドラッグで移動できる（コントロール欄からの再生・音量はそのまま使える）。
export function VideoObject({ object }: Props): JSX.Element {
  const ref = useRef<HTMLVideoElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  const isSelected = useCanvasStore((s) =>
    s.selectedObjectIds.length === 1 && s.selectedObjectIds[0] === object.id
  );

  useObjectDrag({
    objectId: object.id,
    elementRef: ref as unknown as React.RefObject<HTMLElement | null>,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: activeTool === "draw-pen",
  });

  // ビューポート外で自動 pause、再表示時は手動再生に任せる（自動再生は UX を阻害するため）。
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting && !el.paused) {
            el.pause();
          }
        }
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <video
      ref={ref}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="video"
      src={object.src}
      controls
      preload="metadata"
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        background: "#000",
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
        cursor: "move",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        outline: isSelected ? "2px solid var(--color-accent)" : "none",
      }}
    />
  );
}
